// Derives a uniform list of "change entries" from the policy-change form
// values, across all section shapes (record arrays + single objects), so the
// ChangesRequested summary can render them the same way regardless of section.

import { IBusinessPolicyChange } from "../../../../../../../../redux/businessPolicy/businessPolicyChange/businessPolicyChane.types";
import { ChangeSectionKey } from "./WhatChangedBoard";

export interface IChangeFieldRow {
  label: string;
  from?: React.ReactNode;
  to?: React.ReactNode;
  // Whether this field is required but empty (drives "fields missing").
  missing?: boolean;
}

export interface IChangeEntry {
  // Unique id for keying / edit / remove.
  id: string;
  section: ChangeSectionKey;
  subType: string;
  mode: string; // ADD / CHANGE / DELETE
  title: string;
  fields: IChangeFieldRow[];
  missingCount: number;
  // For record-array sections, the index of the record; undefined for
  // single-object sections.
  recordIndex?: number;
}

const asText = (v: any): string => {
  if (v === null || v === undefined || v === "") {
    return "";
  }
  return String(v);
};

const buildFields = (rows: IChangeFieldRow[]): IChangeFieldRow[] =>
  rows.map((r) => ({ ...r, missing: r.missing ?? asText(r.to) === "" }));

const countMissing = (fields: IChangeFieldRow[]): number =>
  fields.filter((f) => f.missing).length;

// Builds a compact address label from the common address parts.
const addressLabel = (parts: (string | null | undefined)[]): string =>
  parts.filter((p) => asText(p) !== "").join(", ");

// Generic helper for the record-array sections that all share the same
// per-record mode ("Add"/"Change") + substitution snapshot shape. Each caller
// supplies how to label the record and which fields to display.
interface IRecordSectionConfig {
  section: ChangeSectionKey;
  subType: string;
  idPrefix: string;
  deleteValue: any;
  deleteLabel: string;
  recordLabel: (rec: any, index: number) => string;
  replaceLabel: string; // e.g. "Coverage"
  recordFields: (rec: any) => IChangeFieldRow[];
}

const deriveRecordSection = (
  records: any[],
  deleteValue: any,
  config: IRecordSectionConfig
): IChangeEntry[] => {
  const entries: IChangeEntry[] = [];
  (records || []).forEach((rec: any, index) => {
    const isSubstitution = rec.mode === "Change";
    const mode = isSubstitution ? "CHANGE" : "ADD";
    const replacedNumber = rec.replaced_delete_value ?? null;
    const fields = buildFields([
      ...(isSubstitution
        ? [
            {
              label: `${config.replaceLabel} to replace`,
              to: replacedNumber,
              missing: false,
            },
          ]
        : []),
      ...config.recordFields(rec),
    ]);
    entries.push({
      id: `${config.idPrefix}-${index}`,
      section: config.section,
      subType: config.subType,
      mode,
      title: `${config.recordLabel(rec, index)} — ${mode}`,
      fields,
      missingCount: countMissing(fields),
      recordIndex: index,
    });
  });

  if (asText(deleteValue) !== "") {
    // A record-level DELETE is only surfaced when it is NOT part of a
    // substitution (substitutions carry the delete pointer alongside a
    // replacement record and already show as CHANGE above).
    const hasSubstitution = (records || []).some(
      (rec: any) => rec.mode === "Change"
    );
    if (!hasSubstitution) {
      const fields = buildFields([
        { label: config.deleteLabel, to: deleteValue, missing: false },
      ]);
      entries.push({
        id: `${config.idPrefix}-delete`,
        section: config.section,
        subType: config.subType,
        mode: "DELETE",
        title: `${config.deleteLabel} ${deleteValue} — DELETE`,
        fields,
        missingCount: 0,
      });
    }
  }

  return entries;
};

export const deriveChangeEntries = (
  values: IBusinessPolicyChange
): IChangeEntry[] => {
  const entries: IChangeEntry[] = [];

  // --- Others: LOA / LOE / Other request type ---
  // The "Others" board card records a type-level request (LOA / LOE / General
  // Change) that has no dedicated field form. It shows whenever the request
  // `type` has been switched away from the default V2 "PolicyChange".
  const OTHER_REQUEST_TYPE_LABELS: Record<string, string> = {
    LOA: "LOA",
    LOE: "LOE",
    GeneralChange: "Other (General Change)",
    New: "New (upload)",
  };
  if (values.type && OTHER_REQUEST_TYPE_LABELS[values.type]) {
    const label = OTHER_REQUEST_TYPE_LABELS[values.type];
    entries.push({
      id: "other_request_type",
      section: "otherRequestType",
      subType: values.sub_type || label,
      mode: "REQUEST",
      title: label,
      fields: [],
      missingCount: 0,
    });
  }

  // --- Cancellation / Reinstatement (single object) ---
  // Captured via the "Cancellation / Reinstatement" board card: a Cancel
  // Reason (cancellation_reason) and a Subtype (sub_type: FLAT / SHORT RATE /
  // PRO-RATA). Shows whenever either has been chosen.
  const CANCELLATION_SUBTYPES = ["FLAT", "SHORT RATE", "PRO-RATA"];
  const hasCancellationSubType = CANCELLATION_SUBTYPES.includes(
    asText(values.sub_type)
  );
  if (asText(values.cancellation_reason) !== "" || hasCancellationSubType) {
    const fields = buildFields([
      { label: "Cancel Reason", to: values.cancellation_reason },
      {
        label: "Subtype",
        to: hasCancellationSubType ? values.sub_type : null,
      },
    ]);
    entries.push({
      id: "cancellation_reinstatement",
      section: "cancellationReinstatement",
      subType: "Cancellation/Reinstatement",
      mode: "CHANGE",
      title: "Cancellation / Reinstatement — CHANGE",
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Named Insured (single object) ---
  if (values.name_change && asText(values.name_change.new_name) !== "") {
    const fields = buildFields([
      { label: "New name", to: values.name_change.new_name },
    ]);
    entries.push({
      id: "name_change",
      section: "nameChange",
      subType: "Name Change",
      mode: "CHANGE",
      title: `Named insured ${values.name_change.new_name || ""} — CHANGE`.trim(),
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Address (single object) ---
  const addressChange = values.address_change;
  const hasAddressChangeData =
    addressChange &&
    [
      addressChange.address,
      addressChange.address_two,
      addressChange.city,
      addressChange.state,
      addressChange.postal_code,
      addressChange.country,
    ].some((v) => asText(v) !== "");
  if (hasAddressChangeData) {
    const fields = buildFields([
      { label: "Address type", to: addressChange.address_type, missing: false },
      { label: "Address", to: addressChange.address },
      { label: "Address 2", to: addressChange.address_two, missing: false },
      { label: "City", to: addressChange.city },
      { label: "Province/State", to: addressChange.state },
      { label: "Postal code", to: addressChange.postal_code },
      { label: "Country", to: addressChange.country, missing: false },
    ]);
    entries.push({
      id: "address_change",
      section: "addressChange",
      subType: "Address Change",
      mode: "CHANGE",
      title: "Address — CHANGE",
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Payment Method / Credit Card (single object) ---
  const cc: any = values.credit_card_info;
  const hasPaymentInfo =
    cc &&
    [
      cc.payment_type,
      cc.cc,
      cc.expiry,
      cc.cvv,
      cc.account_holder_name,
      cc.bank_name,
      cc.account_number,
      cc.institution_number,
      cc.transit_number,
    ].some((v) => asText(v) !== "");
  if (hasPaymentInfo) {
    // Human-readable label for the chosen payment type.
    const paymentTypeLabel =
      cc.payment_type === "Bank"
        ? "Bank (PAD)"
        : cc.payment_type === "Credit Card"
        ? "Credit card"
        : cc.payment_type || "";
    const maskedCard =
      asText(cc.cc) !== "" ? `•••• ${asText(cc.cc).slice(-4)}` : "";

    // Only show the fields relevant to the selected payment type, so the
    // summary matches what the user actually filled in (mirrors Automobile V2).
    let typeFields: { label: string; to: any; missing?: boolean }[] = [];
    if (cc.payment_type === "Bank") {
      typeFields = [
        { label: "Bank name", to: cc.bank_name },
        { label: "Account holder name", to: cc.account_holder_name },
        { label: "Account number", to: cc.account_number },
        { label: "Institution number", to: cc.institution_number },
        { label: "Transit number", to: cc.transit_number },
      ];
    } else if (cc.payment_type === "Credit Card") {
      typeFields = [
        { label: "Card number", to: maskedCard, missing: false },
        { label: "Expiry", to: cc.expiry },
        { label: "Account holder name", to: cc.account_holder_name },
      ];
    } else {
      // Cheque / Other / not yet chosen.
      typeFields = [
        { label: "Account holder name", to: cc.account_holder_name },
        { label: "Bank name", to: cc.bank_name },
      ];
    }

    const fields = buildFields([
      { label: "Payment type", to: paymentTypeLabel, missing: false },
      ...typeFields,
    ]);
    entries.push({
      id: "payment_method_change",
      section: "paymentMethodChange",
      subType: "Payment Method Change",
      mode: "CHANGE",
      title: `Payment method${
        paymentTypeLabel ? ` (${paymentTypeLabel})` : ""
      } — CHANGE`,
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Risk Location (record array) ---
  entries.push(
    ...deriveRecordSection(
      values.risk_location_change?.records as any[],
      values.risk_location_change?.delete_risk_location,
      {
        section: "riskLocationChange",
        subType: "Risk Location Change",
        idPrefix: "risk_location_change",
        deleteValue: values.risk_location_change?.delete_risk_location,
        deleteLabel: "Risk location",
        replaceLabel: "Risk location",
        recordLabel: (rec, index) =>
          `Risk location ${
            addressLabel([rec.address, rec.city, rec.province_or_state]) ||
            `#${index + 1}`
          }`,
        recordFields: (rec) => [
          { label: "Location #", to: rec.location_number, missing: false },
          { label: "Address", to: rec.address },
          { label: "City", to: rec.city },
          { label: "Province/State", to: rec.province_or_state },
          { label: "Postal code", to: rec.postal_code },
        ],
      }
    )
  );

  // --- Coverage (record array) ---
  entries.push(
    ...deriveRecordSection(
      values.coverage_change?.records as any[],
      values.coverage_change?.delete_coverage,
      {
        section: "coverageChange",
        subType: "Coverage Change",
        idPrefix: "coverage_change",
        deleteValue: values.coverage_change?.delete_coverage,
        deleteLabel: "Coverage",
        replaceLabel: "Coverage",
        recordLabel: (rec, index) =>
          `Coverage ${
            rec.coverage_number ? `#${rec.coverage_number}` : `#${index + 1}`
          }`,
        recordFields: (rec) => [
          { label: "Coverage #", to: rec.coverage_number, missing: false },
          { label: "Coverage type", to: rec.coverage_type, missing: false },
        ],
      }
    )
  );

  // --- Interested Party (record array) ---
  entries.push(
    ...deriveRecordSection(
      values.interested_party_change?.records as any[],
      values.interested_party_change?.delete_interested_party,
      {
        section: "interestedPartyChange",
        subType: "Interested Party Change",
        idPrefix: "interested_party_change",
        deleteValue: values.interested_party_change?.delete_interested_party,
        deleteLabel: "Interested party",
        replaceLabel: "Interested party",
        recordLabel: (rec, index) =>
          `Interested party ${
            rec.interested_party_name ||
            rec.interested_party_code ||
            `#${rec.incremental_number ?? index + 1}`
          }`,
        recordFields: (rec) => [
          {
            label: "Interested party #",
            to: rec.incremental_number,
            missing: false,
          },
          {
            label: "Name",
            to: rec.interested_party_name,
            missing: false,
          },
          {
            label: "Interested party code",
            to: rec.interested_party_code,
            missing: false,
          },
        ],
      }
    )
  );

  // --- Equipment (record array) ---
  entries.push(
    ...deriveRecordSection(
      values.equipment_change?.records as any[],
      values.equipment_change?.delete_equipment,
      {
        section: "equipmentChange",
        subType: "Equipment Change",
        idPrefix: "equipment_change",
        deleteValue: values.equipment_change?.delete_equipment,
        deleteLabel: "Equipment",
        replaceLabel: "Equipment",
        recordLabel: (rec, index) =>
          `Equipment #${rec.incremental_number ?? index + 1}`,
        recordFields: (rec) => [
          { label: "Equipment #", to: rec.incremental_number, missing: false },
          { label: "Description", to: rec.description, missing: false },
        ],
      }
    )
  );

  // --- Tool Floater (record array) ---
  entries.push(
    ...deriveRecordSection(
      values.tool_floater_change?.records as any[],
      values.tool_floater_change?.delete_tool_floater,
      {
        section: "toolFloaterChange",
        subType: "Tool Floater Change",
        idPrefix: "tool_floater_change",
        deleteValue: values.tool_floater_change?.delete_tool_floater,
        deleteLabel: "Tool floater",
        replaceLabel: "Tool floater",
        recordLabel: (rec, index) =>
          `Tool floater #${rec.incremental_number ?? index + 1}`,
        recordFields: (rec) => [
          {
            label: "Tool floater #",
            to: rec.incremental_number,
            missing: false,
          },
          { label: "Description", to: rec.description, missing: false },
        ],
      }
    )
  );

  // --- Fine Art (record array) ---
  entries.push(
    ...deriveRecordSection(
      values.fine_art_change?.records as any[],
      values.fine_art_change?.delete_fine_art,
      {
        section: "fineArtChange",
        subType: "Fine Art Change",
        idPrefix: "fine_art_change",
        deleteValue: values.fine_art_change?.delete_fine_art,
        deleteLabel: "Fine art",
        replaceLabel: "Fine art",
        recordLabel: (rec, index) =>
          `Fine art #${rec.incremental_number ?? index + 1}`,
        recordFields: (rec) => [
          { label: "Fine art #", to: rec.incremental_number, missing: false },
          { label: "Description", to: rec.description, missing: false },
        ],
      }
    )
  );

  return entries;
};
